/**
 * Centralized API configuration for MotoTrips
 * 
 * This module handles authentication token management for cross-domain deployments
 * where cookies may not work due to different domains between frontend and backend.
 * 
 * The session token is stored in localStorage and sent as Authorization header.
 */
import axios from 'axios';
import { BACKEND_URL } from "../config";

const API_BASE = BACKEND_URL;

// Token storage keys
const TOKEN_KEY = 'mototrips_session_token';

/**
 * Get the stored session token
 */
export const getSessionToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Store the session token
 */
export const setSessionToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

/**
 * Clear the session token
 */
export const clearSessionToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Check if user has a stored session token
 */
export const hasSessionToken = () => {
  return !!getSessionToken();
};

/** Emit a global event so AuthContext can react to expired sessions */
const emitAuthExpired = () => {
  window.dispatchEvent(new CustomEvent('auth:expired'));
};

/**
 * "Act as user" (owner repair mode). When the platform owner is editing
 * another user's canto, this holds that user's id and the request interceptor
 * appends `?as_user=<id>` to every API call automatically.
 */
let _actAsUserId = null;
export const setActAsUser = (userId) => { _actAsUserId = userId || null; };
export const getActAsUser = () => _actAsUserId;
export const clearActAsUser = () => { _actAsUserId = null; };

/** Routes that should NOT trigger auto-redirect on 401 */
const AUTH_ROUTES = ['/auth/login', '/auth/register', '/auth/google', '/auth/me', '/auth/logout'];
const isAuthRoute = (url) => AUTH_ROUTES.some((r) => url?.includes(r));

/**
 * Create axios instance with auth interceptor
 */
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Still send cookies when possible
});

// Request interceptor to add Authorization header
api.interceptors.request.use(
  (config) => {
    const token = getSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Owner "act as user" repair mode: append as_user to every call.
    if (_actAsUserId && !isAuthRoute(config.url)) {
      config.params = { ...(config.params || {}), as_user: _actAsUserId };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isAuthRoute(error.config?.url)) {
      // Only redirect if user HAD a session (expired/invalid token)
      // Anonymous users (no token) should NOT be redirected
      const hadSession = !!getSessionToken();
      if (hadSession) {
        clearSessionToken();
        window.location.href = '/login';
        return new Promise(() => {});
      }
    }
    // Plan enforcement errors — show toast with upgrade link
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      if (detail?.code === "PLAN_LIMIT_REACHED" || detail?.code === "PLAN_FEATURE_DISABLED") {
        import("sonner").then(({ toast }) => toast.error(detail.message, {
          duration: 6000,
          action: {
            label: "Ver planos",
            onClick: () => { window.location.href = "/planos"; },
          },
        }));
      }
    }
    // Rate limit (429) — daily quota hit. Backend sends a PT message in
    // `detail` string explaining which quota was reached; we surface it as
    // a toast and offer a link to the plans page where the user can upgrade
    // to loosen their limits.
    if (error.response?.status === 429) {
      const detail = error.response?.data?.detail;
      const message = (typeof detail === "string" && detail)
        ? detail
        : (detail?.message || "Limite diário atingido. Tenta novamente amanhã.");
      import("sonner").then(({ toast }) => toast.error(message, {
        duration: 7000,
        action: {
          label: "Ver planos",
          onClick: () => { window.location.href = "/planos"; },
        },
      }));
    }
    return Promise.reject(error);
  }
);

/**
 * Wrapper around fetch() that handles 401 / 403 / 429 automatically.
 * - 401: redirects to /login (callers never see the error).
 * - 403 PLAN_LIMIT_REACHED / PLAN_FEATURE_DISABLED: shows toast with upgrade link.
 * - 429: shows rate-limit toast with upgrade link.
 * Non-handled errors are returned as the Response object to the caller.
 */
export const authFetch = async (url, options = {}) => {
  const token = getSessionToken();
  const headers = { ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !isAuthRoute(url) && token) {
    clearSessionToken();
    window.location.href = '/login';
    return new Promise(() => {}); // never resolve — page is navigating away
  }

  // Mirror axios interceptor for plan/rate-limit toasts on raw fetch callers.
  if (res.status === 403 || res.status === 429) {
    try {
      // Clone so the caller still has the body to read
      const clone = res.clone();
      const body = await clone.json();
      const detail = body?.detail;
      if (res.status === 403 && (detail?.code === "PLAN_LIMIT_REACHED" || detail?.code === "PLAN_FEATURE_DISABLED")) {
        import("sonner").then(({ toast }) => toast.error(detail.message, {
          duration: 6000,
          action: { label: "Ver planos", onClick: () => { window.location.href = "/planos"; } },
        }));
      } else if (res.status === 429) {
        const message = (typeof detail === "string" && detail) ? detail : (detail?.message || "Limite diário atingido. Tenta novamente amanhã.");
        import("sonner").then(({ toast }) => toast.error(message, {
          duration: 7000,
          action: { label: "Ver planos", onClick: () => { window.location.href = "/planos"; } },
        }));
      }
    } catch {
      /* body wasn't JSON — ignore */
    }
  }

  return res;
};

export default api;
